import { useDoubleClickStore } from "../../../../Store/DoubleClickStore";
import { BoostTask } from "../../types/boost";

// providers/RichAdsManualBoostProvider.ts

declare global {
  interface Window {
    RichAdsControllerInstance?: any;
  }
}

export class RichAdsBoostProvider {
  initialized = false;
  controllerInstance: any = null;
  private initializationPromise: Promise<void> | null = null;
  private closeButtonObserver: MutationObserver | null = null;

  getName() {
    return "RichAds_CPC";
  }

  private isAndroidPlatform(): boolean {
    const platform = window.Telegram.WebApp.platform.toLowerCase();
    return platform === "android" || platform === "tdesktop";
  }

  async initialize(): Promise<void> {
    if (!this.isAndroidPlatform()) {
      return Promise.reject("platform_not_supported");
    }

    if (this.initialized) return Promise.resolve();
    if (this.initializationPromise) return this.initializationPromise;

    if (typeof window === "undefined") {
      console.warn("RichAdsProvider: Cannot initialize on server-side.");
      return Promise.reject("server_side");
    }

    this.initializationPromise = new Promise((resolve, reject) => {
      console.log(
        "RichAdsProvider: Checking for pre-initialized instance (window.RichAdsControllerInstance)..."
      );

      const checkInstance = (attempt = 1) => {
        if (window.RichAdsControllerInstance) {
          console.log(
            "RichAdsProvider: Found pre-initialized RichAdsControllerInstance."
          );
          this.controllerInstance = window.RichAdsControllerInstance;
          this.initialized = true;
          resolve();
        } else if (
          window.hasOwnProperty("RichAdsControllerInstance") &&
          window.RichAdsControllerInstance === null
        ) {
          console.error(
            "RichAdsProvider: Initialization failed in index.html (instance is null)."
          );
          this.initializationPromise = null;
          reject("init_failed_in_html");
        } else if (attempt < 5) {
          console.log(
            `RichAdsProvider: Instance not found yet. Retrying (attempt ${
              attempt + 1
            })...`
          );
          setTimeout(() => checkInstance(attempt + 1), 300);
        } else {
          console.error(
            "RichAdsProvider: window.RichAdsControllerInstance not found after multiple attempts."
          );
          this.initializationPromise = null;
          reject("instance_not_found");
        }
      };

      checkInstance();
    });

    try {
      await this.initializationPromise;
    } catch (error) {
      console.error("RichAdsProvider: Initialization check failed:", error);
      this.initializationPromise = null;
      return Promise.reject(error);
    }

    return this.initializationPromise;
  }

  private setupCloseButtonObserver(): void {
    if (this.closeButtonObserver) {
      this.closeButtonObserver.disconnect();
    }

    this.closeButtonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          const closeButton = document.getElementById(
            "el-notification-close-btn"
          );
          if (closeButton) {
            console.log("RichAds: Close button found, adding click handler");
            closeButton.addEventListener("click", () => {
              console.log("RichAds: Close button clicked");
              const event = new CustomEvent("richads-close-clicked");
              window.dispatchEvent(event);
            });
          }

          // Добавляем обработчик для баннера
          const banner = document.querySelector(".first-block");
          if (banner) {
            console.log("RichAds: Banner found, adding click handler");
            banner.addEventListener("click", () => {
              console.log("RichAds: Banner clicked");
              const event = new CustomEvent("richads-close-clicked");
              window.dispatchEvent(event);
            });
          }

          // Отключаем observer только если нашли оба элемента
          if (closeButton && banner) {
            this.closeButtonObserver?.disconnect();
          }
        }
      });
    });

    this.closeButtonObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  async showAd(): Promise<boolean> {
    if (!this.isAndroidPlatform()) {
      return false;
    }

    this.setupCloseButtonObserver();

    try {
      await this.initialize();
    } catch (initError) {
      console.error(
        "RichAdsProvider: Cannot show ad, initialization check failed.",
        initError
      );
      return false;
    }

    if (!this.controllerInstance) {
      console.warn(
        "RichAdsProvider: Cannot show ad, controller instance is null."
      );
      return false;
    }

    console.log("RichAdsProvider: Calling triggerNativeNotification...");
    try {
      const result = await this.controllerInstance.triggerNativeNotification(
        false
      );
      console.log(
        "RichAdsProvider: triggerNativeNotification finished successfully. Result:",
        result
      );
      return true;
    } catch (e) {
      console.warn(
        "RichAdsProvider: triggerNativeNotification failed or ad not found. Error:",
        e
      );
      return false;
    }
  }

  async getAvailableTasks(miningId: number): Promise<BoostTask[]> {
    if (!this.isAndroidPlatform()) {
      return [];
    }

    try {
      await this.initialize();
    } catch (initError) {
      console.error(
        "RichAdsProvider: Cannot get tasks, initialization check failed.",
        initError
      );
      return [];
    }

    if (!this.initialized || !this.controllerInstance) {
      console.warn(
        "RichAdsProvider: Cannot get tasks, controller not initialized."
      );
      return [];
    }

    try {
      console.log("RichAdsProvider: Instance found, offering ad task.");
      const tasks: BoostTask[] = [
        {
          id: `richads_task_${miningId}`,
          title: "Бонус за рекламу",
          description: "Посмотрите рекламу, чтобы получить бонус",
          provider: this.getName(),
          onClick: async () => {
            const result = await this.showAd();
            return result ? "success" : "ads_not_found";
          },
        },
      ];
      return Promise.resolve(tasks);
    } catch (error) {
      console.error("RichAdsProvider: Error constructing boost task:", error);
      return Promise.resolve([]);
    }
  }

  resetError() {
    console.log("RichAdsProvider: Resetting potential error state.");
    if (!this.initialized && this.initializationPromise) {
      console.log(
        "RichAdsProvider: Resetting initialization promise due to resetError call."
      );
      this.initializationPromise = null;
    }
  }
}
